import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { ThumbsUp, ThumbsDown, PaperPlaneRight, Info, SpeakerHigh, SpeakerSlash, Waveform } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

/**
 * Moe Chat Drawer — slides in from the right.
 * Sprint 2: accepts an audio `pendingBlob` from the Mowing Moe strip,
 * sends it to /api/moe/ask-voice, shows transcript + reply, and speaks
 * Moe's answer in "radio voice" via browser SpeechSynthesis.
 */
export const MoeChatDrawer = ({
  open, onOpenChange, onAnswering,
  pendingBlob, onBlobConsumed,
  voice, wakeWord,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [voiceOutOn, setVoiceOutOn] = useState(() => localStorage.getItem('moe.voiceOut') !== '0');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Consume a pending audio blob when the drawer opens
  useEffect(() => {
    if (!open || !pendingBlob) return;
    sendVoice(pendingBlob);
    onBlobConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingBlob]);

  const speakIfEnabled = (text) => {
    if (voiceOutOn && voice?.speak) voice.speak(text, { voicePref: 'radio' });
  };

  const sendVoice = async (blob) => {
    setSending(true);
    onAnswering?.(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'question.webm');
      const res = await axios.post(`${API_URL}/api/moe/ask-voice`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        params: conversationId ? { conversation_id: conversationId } : {},
      });
      setConversationId(res.data.conversation_id);
      if (res.data.transcript) {
        setMessages(prev => [...prev, { role: 'user', content: res.data.transcript, id: `u-${Date.now()}`, voice: true }]);
      }
      setMessages(prev => [...prev, {
        role: 'moe',
        content: res.data.answer,
        id: res.data.message_id,
        route: res.data.route,
        guide_id: res.data.guide_id,
        grounded_in: res.data.grounded_in,
      }]);
      speakIfEnabled(res.data.answer);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Voice send failed — try text.');
    } finally {
      setSending(false);
      onAnswering?.(false);
    }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: q, id: `u-${Date.now()}` }]);
    setSending(true);
    onAnswering?.(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/moe/ask`,
        { question: q, conversation_id: conversationId },
        { withCredentials: true }
      );
      setConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, {
        role: 'moe',
        content: res.data.answer,
        id: res.data.message_id,
        route: res.data.route,
        guide_id: res.data.guide_id,
        grounded_in: res.data.grounded_in,
      }]);
      speakIfEnabled(res.data.answer);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Moe is offline — try again.');
    } finally {
      setSending(false);
      onAnswering?.(false);
    }
  };

  const rate = async (message_id, rating) => {
    try {
      await axios.post(`${API_URL}/api/moe/feedback`, { message_id, rating }, { withCredentials: true });
      toast.success(rating === 'helpful' ? 'Thanks!' : 'Noted — I\'ll improve.');
    } catch { toast.error('Feedback failed'); }
  };

  const toggleVoiceOut = (next) => {
    setVoiceOutOn(next);
    localStorage.setItem('moe.voiceOut', next ? '1' : '0');
    if (!next) voice?.shutUp?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="moe-chat-drawer">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2 text-base">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Moe
            <span className="text-xs font-normal text-muted-foreground">alpha</span>
            <div className="flex-1" />
            {/* Voice out toggle */}
            <button
              data-testid="moe-voice-out-toggle"
              onClick={() => toggleVoiceOut(!voiceOutOn)}
              aria-label={voiceOutOn ? 'Mute Moe' : 'Unmute Moe'}
              className="p-1 rounded hover:bg-muted transition-colors"
              title={voiceOutOn ? 'Moe will speak answers (click to mute)' : 'Moe is muted (click to enable radio voice)'}
            >
              {voiceOutOn
                ? <SpeakerHigh size={16} weight={voice?.speaking ? 'fill' : 'regular'} className={voice?.speaking ? 'text-emerald-500 animate-pulse' : 'text-muted-foreground'} />
                : <SpeakerSlash size={16} className="text-muted-foreground" />}
            </button>
          </SheetTitle>
        </SheetHeader>

        {/* Wake-word opt-in row */}
        {wakeWord?.supported && (
          <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between" data-testid="wake-word-row">
            <div className="flex items-center gap-2 text-xs">
              <Waveform size={14} className={wakeWord.listening ? 'text-emerald-500 animate-pulse' : 'text-muted-foreground'} weight="bold" />
              <span className="font-medium">"Hey Moe" wake word</span>
              {wakeWord.listening && <span className="text-[10px] text-emerald-600 font-mono">listening</span>}
            </div>
            <Switch
              data-testid="wake-word-switch"
              checked={wakeWord.enabled}
              onCheckedChange={wakeWord.setEnabled}
            />
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">
              <p className="mb-2">Ask me anything about your lawn.</p>
              <p className="text-xs mb-3">Try: "When did I last fertilize?" · "What's next?" · "How big is my lawn?"</p>
              <p className="text-xs italic">🎤 Hold the "Hey Moe" button on the strip to speak, or type below.</p>
            </div>
          )}
          {messages.map(m => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                m.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-muted text-foreground'
              }`} data-testid={`moe-msg-${m.role}`}>
                {m.voice && m.role === 'user' && (
                  <div className="flex items-center gap-1 mb-0.5 text-[10px] uppercase opacity-80">
                    <Waveform size={10} weight="bold" /> voice
                  </div>
                )}
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.role === 'moe' && (
                  <div className="flex items-center gap-2 mt-2 pt-1.5 border-t border-border/30">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-mono">
                      {m.route === 'data' && 'from your data'}
                      {m.route === 'guide' && 'routed to guide'}
                      {m.route === 'llm' && 'thinking'}
                      {m.route === 'transcription_failed' && 'heard nothing'}
                      {(m.route === 'llm_error' || m.route === 'llm_unavailable' || m.route === 'llm_unconfigured') && 'offline'}
                    </span>
                    <div className="flex-1" />
                    <button onClick={() => voice?.speak?.(m.content, { voicePref: 'radio' })} className="text-muted-foreground hover:text-emerald-500" aria-label="Replay">
                      <SpeakerHigh size={12} weight="bold" />
                    </button>
                    <button onClick={() => rate(m.id, 'helpful')} className="text-muted-foreground hover:text-emerald-500" aria-label="Helpful">
                      <ThumbsUp size={12} weight="bold" />
                    </button>
                    <button onClick={() => rate(m.id, 'not_helpful')} className="text-muted-foreground hover:text-red-500" aria-label="Not helpful">
                      <ThumbsDown size={12} weight="bold" />
                    </button>
                    {m.grounded_in?.length > 0 && (
                      <span className="text-muted-foreground hover:text-foreground cursor-help"
                        title={m.grounded_in.map(g => `${g.type}: ${JSON.stringify(g.value).slice(0, 60)}`).join('\n')}>
                        <Info size={12} weight="bold" />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl px-3.5 py-2 text-sm text-muted-foreground flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" style={{ animationDelay: '0.15s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          )}
        </div>

        <div className="px-3 py-3 border-t bg-background flex items-center gap-2">
          <Input
            data-testid="moe-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask Moe…"
            disabled={sending}
            maxLength={800}
            className="flex-1"
          />
          <Button data-testid="moe-send-btn" size="sm" onClick={send} disabled={sending || !input.trim()}>
            <PaperPlaneRight size={16} weight="fill" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
